import {useMemo} from "react";
import {useAuth} from "../context/AuthContext";
import {useBusiness} from "../context/BusinessContext";
import {ROLES} from "../lib/roles";

export type BusinessAccess = {
    /** False while auth or the business list is still hydrating. */
    isReady: boolean;
    /** Roles held at the active business. */
    roles: Set<string>;
    /** Permissions granted at the active business. */
    permissions: Set<string>;
    isAdmin: boolean;
    isEmployee: boolean;
    /** Admins pass unconditionally; employees need one of the listed permissions. */
    canAccess: (...required: string[]) => boolean;
};

/**
 * Resolves what the user may do at the business they are currently working in.
 *
 * A user can hold roles at several businesses at once, so the account-wide roles and
 * permissions on `user` are a union across all of them and must not gate a single
 * business. The active business reports its own roles and permissions, and those win.
 * The account-wide values are used only when it reports neither, so a backend that
 * doesn't send them cannot lock anyone out. ADMINISTRATOR is platform-wide and always
 * carries over.
 */
export function useBusinessAccess(): BusinessAccess {
    const {user} = useAuth();
    const {activeBusiness, isReady} = useBusiness();

    const accountRoles = user?.roles;
    const accountPermissions = user?.permissions;
    const businessRoles = activeBusiness?.roles;
    const businessPermissions = activeBusiness?.permissions;

    return useMemo(() => {
        const scoped = !!(businessRoles || businessPermissions);

        const roles = new Set<string>(scoped ? businessRoles ?? [] : accountRoles ?? []);
        const permissions = new Set<string>(
            scoped ? businessPermissions ?? [] : accountPermissions ?? []
        );
        if (scoped && (accountRoles ?? []).includes(ROLES.ADMIN)) roles.add(ROLES.ADMIN);

        const isAdmin = roles.has(ROLES.ADMIN) || roles.has(ROLES.BUSINESS);
        const isEmployee = roles.has(ROLES.EMPLOYEE);

        // An employee needs the permission granted at this business. Holding the role
        // on its own grants nothing, and a permission without the role grants nothing.
        const canAccess = (...required: string[]) =>
            isAdmin || (isEmployee && required.some((p) => permissions.has(p)));

        return {isReady, roles, permissions, isAdmin, isEmployee, canAccess};
    }, [isReady, accountRoles, accountPermissions, businessRoles, businessPermissions]);
}
